<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import type { ShipmentFormPageContext } from '@/components/shipment/form/types/shipment-form-page-context';
import { useShipmentFormStopsSection } from '@/composables/useShipmentFormStopsSection';
import { useShipmentFormStopsSession } from '@/composables/useShipmentFormStopsSession';
import ShipmentFormStopsList from '@/components/shipment/form/stops/ShipmentFormStopsList.vue';
import ShipmentFormRoutePreviewPanel from '@/components/shipment/form/stops/ShipmentFormRoutePreviewPanel.vue';
import StopTimeAutofillConflictDialog from '@/components/shipment/form/stops/StopTimeAutofillConflictDialog.vue';

const props = defineProps<{
  pageContext?: ShipmentFormPageContext;
}>();

const pageContextRef = computed(() => props.pageContext);
const {
  stops,
  stopsResolver,
  stopFieldErrorsFor,
  suppressPlannedTimeManualMark,
  onUserPlannedTimeEdit,
  anchorConflictDialogVisible,
  conflictResolutionChoice,
  onAnchorConflictApply,
  onAnchorConflictCancel,
  COMPOSER_DEBOUNCE,
  editContext,
  stopsPort,
  scene,
} = useShipmentFormStopsSection(pageContextRef);

const {
  hasGoogleKey,
  geofences,
  geofencesLoading,
  placesAutocompleteDisabled,
  corridorBufferM,
  routingStrategy,
  routeError,
  routeWarning,
  mapProvider,
  showMapBusyOverlay,
  pathIds,
  pathGroups,
  pathUi,
  selectedByPathId,
  routingLoading,
  showAlternativesSection,
  showAlternativesHeaderSpinner,
  commitCorridorBufferImmediately,
  onMapReady,
  togglePath,
} = scene;

const stopsSession = useShipmentFormStopsSession();

onMounted(() => {
  stopsSession?.registerStopsPort(stopsPort);
});

watch(
  () => stopsPort,
  () => {
    stopsSession?.registerStopsPort(stopsPort);
  },
  { deep: true },
);

const stopsListRef = ref<{ focusFirstField?: () => boolean } | null>(null);
const routePreviewRef = ref<{ focusFirstRouteControl?: () => boolean } | null>(
  null,
);

function focusFirstField(): boolean {
  return stopsListRef.value?.focusFirstField?.() ?? false;
}

function focusFirstRouteControl(): boolean {
  return routePreviewRef.value?.focusFirstRouteControl?.() ?? false;
}

defineExpose({
  ...stopsPort,
  focusFirstField,
  focusFirstRouteControl,
});
</script>

<template>
  <div>
    <p v-if="!hasGoogleKey" class="mb-4 text-xs text-amber-800">
      Set
      <code class="rounded bg-neutral-100 px-1 py-0.5 text-xs"
        >VITE_GOOGLE_MAPS_API_KEY</code
      >
      and enable Routes API, Places API, and Maps JavaScript API.
    </p>

    <ShipmentFormStopsList
      ref="stopsListRef"
      :stops="stops"
      :geofences="geofences"
      :geofences-loading="geofencesLoading"
      :places-disabled="placesAutocompleteDisabled"
      :stops-resolver="stopsResolver"
      :stop-field-errors-for="stopFieldErrorsFor"
      :suppress-planned-time-manual-mark="suppressPlannedTimeManualMark"
      :edit-context="editContext"
      @planned-time-user-edit="onUserPlannedTimeEdit"
    />

    <ShipmentFormRoutePreviewPanel
      ref="routePreviewRef"
      v-model:corridor-buffer-m="corridorBufferM"
      :routing-strategy="routingStrategy"
      :route-error="routeError"
      :route-warning="routeWarning"
      :map-provider="mapProvider"
      :show-map-busy-overlay="showMapBusyOverlay"
      :path-ids="pathIds"
      :path-groups="pathGroups"
      :path-ui="pathUi"
      :selected-by-path-id="selectedByPathId"
      :routing-loading="routingLoading"
      :show-alternatives-section="showAlternativesSection"
      :show-alternatives-header-spinner="showAlternativesHeaderSpinner"
      :corridor-buffer-debounce-ms="COMPOSER_DEBOUNCE.corridorBufferMs"
      :commit-corridor-buffer-immediately="commitCorridorBufferImmediately"
      @map-ready="onMapReady"
      @toggle-path="togglePath"
    />

    <StopTimeAutofillConflictDialog
      v-model:visible="anchorConflictDialogVisible"
      v-model:conflict-resolution-choice="conflictResolutionChoice"
      @apply="onAnchorConflictApply"
      @cancel="onAnchorConflictCancel"
    />
  </div>
</template>
